import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarBlank,
  Plus,
  CaretLeft,
  CaretRight,
  Clock,
  Car,
  User,
  Trash,
  PencilSimple,
  Check,
  X
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MOIS_NOMS = ["", "Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin", "Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"];
const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const TYPE_COLORS = { essai: "bg-blue-500", livraison: "bg-emerald-500", visite: "bg-violet-500", autre: "bg-slate-500" };
const TYPE_LABELS = { essai: "Essai", livraison: "Livraison", visite: "Visite", autre: "Autre" };
const STATUT_LABELS = { planifie: "Planifie", confirme: "Confirme", annule: "Annule", termine: "Termine" };

export default function Calendrier() {
  const { user } = useAuth();
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [rdvs, setRdvs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRdv, setEditingRdv] = useState(null);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ titre: "", type_rdv: "essai", client_nom: "", vehicle_nom: "", date_rdv: "", duree_minutes: 60, notes: "", statut: "planifie" });

  const loadRdvs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rdv`, { params: { mois, annee } });
      setRdvs(res.data);
    } catch {}
  }, [mois, annee]);

  useEffect(() => { loadRdvs(); }, [loadRdvs]);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [cRes, vRes] = await Promise.all([
          axios.get(`${API}/clients`),
          axios.get(`${API}/vehicles`)
        ]);
        setClients(cRes.data?.data || cRes.data || []);
        setVehicles((vRes.data?.data || vRes.data || []).map(v => ({ ...v, label: `${v.marque} ${v.modele} (${v.reference_interne})` })));
      } catch {}
    };
    loadRefs();
  }, []);

  const openNewRdv = (day) => {
    const dateStr = `${annee}-${String(mois).padStart(2, "0")}-${String(day).padStart(2, "0")}T10:00`;
    setForm({ titre: "", type_rdv: "essai", client_nom: "", vehicle_nom: "", date_rdv: dateStr, duree_minutes: 60, notes: "", statut: "planifie" });
    setEditingRdv(null);
    setShowForm(true);
  };

  const openEditRdv = (rdv) => {
    setForm({
      titre: rdv.titre || "",
      type_rdv: rdv.type_rdv || "essai",
      client_nom: rdv.client_nom || "",
      vehicle_nom: rdv.vehicle_nom || "",
      date_rdv: rdv.date_rdv ? rdv.date_rdv.slice(0, 16) : "",
      duree_minutes: rdv.duree_minutes || 60,
      notes: rdv.notes || "",
      statut: rdv.statut || "planifie"
    });
    setEditingRdv(rdv);
    setShowForm(true);
  };

  const saveRdv = async () => {
    if (!form.titre || !form.date_rdv) { toast.error("Titre et date requis"); return; }
    try {
      if (editingRdv) {
        await axios.put(`${API}/rdv/${editingRdv.rdv_id}`, form);
        toast.success("RDV mis a jour");
      } else {
        await axios.post(`${API}/rdv`, form);
        toast.success("RDV cree");
      }
      setShowForm(false);
      loadRdvs();
    } catch { toast.error("Erreur sauvegarde RDV"); }
  };

  const deleteRdv = async (rdvId) => {
    try {
      await axios.delete(`${API}/rdv/${rdvId}`);
      toast.success("RDV supprime");
      loadRdvs();
    } catch { toast.error("Erreur suppression"); }
  };

  // Calendar grid
  const firstDay = new Date(annee, mois - 1, 1);
  const lastDay = new Date(annee, mois, 0);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday = 0
  const totalDays = lastDay.getDate();
  const today = new Date();
  const isToday = (d) => d === today.getDate() && mois === today.getMonth() + 1 && annee === today.getFullYear();

  const getRdvsForDay = (day) => {
    return rdvs.filter(r => {
      if (!r.date_rdv) return false;
      const d = new Date(r.date_rdv);
      return d.getDate() === day && d.getMonth() + 1 === mois && d.getFullYear() === annee;
    });
  };

  const prevMonth = () => { if (mois === 1) { setMois(12); setAnnee(annee - 1); } else setMois(mois - 1); };
  const nextMonth = () => { if (mois === 12) { setMois(1); setAnnee(annee + 1); } else setMois(mois + 1); };

  return (
    <div className="space-y-4" data-testid="calendrier-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Calendrier</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Planifiez vos rendez-vous</p>
        </div>
        <Button onClick={() => openNewRdv(today.getDate())} className="btn-primary" data-testid="new-rdv-btn">
          <Plus size={16} className="mr-1.5" /> Nouveau RDV
        </Button>
      </div>

      {/* Month navigation */}
      <div className="card p-3 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month"><CaretLeft size={18} /></Button>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{MOIS_NOMS[mois]} {annee}</h2>
        <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month"><CaretRight size={18} /></Button>
      </div>

      {/* Calendar grid */}
      <div className="card overflow-hidden">
        <div className="grid grid-cols-7">
          {JOURS.map(j => (
            <div key={j} className="px-2 py-2 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">{j}</div>
          ))}
          {Array.from({ length: startOffset }).map((_, i) => (
            <div key={`empty-${i}`} className="min-h-[80px] border-b border-r border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50" />
          ))}
          {Array.from({ length: totalDays }).map((_, i) => {
            const day = i + 1;
            const dayRdvs = getRdvsForDay(day);
            return (
              <div key={day}
                onClick={() => openNewRdv(day)}
                className={`min-h-[80px] border-b border-r border-slate-100 dark:border-slate-800 p-1 cursor-pointer hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors ${isToday(day) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                data-testid={`day-${day}`}>
                <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday(day) ? 'bg-blue-600 text-white' : 'text-slate-700 dark:text-slate-300'}`}>{day}</div>
                {dayRdvs.slice(0, 3).map(r => (
                  <div key={r.rdv_id} onClick={(e) => { e.stopPropagation(); openEditRdv(r); }}
                    className={`text-[10px] text-white rounded px-1 py-0.5 mb-0.5 truncate cursor-pointer ${TYPE_COLORS[r.type_rdv] || 'bg-slate-500'} ${r.statut === 'annule' ? 'opacity-40 line-through' : ''}`}
                    data-testid={`rdv-${r.rdv_id}`}>
                    {r.date_rdv ? new Date(r.date_rdv).toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" }) : ""} {r.titre}
                  </div>
                ))}
                {dayRdvs.length > 3 && <p className="text-[10px] text-slate-400">+{dayRdvs.length - 3} autres</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming RDVs */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
          <Clock size={16} className="text-blue-600" /> Prochains rendez-vous
        </h3>
        <div className="space-y-2">
          {rdvs.filter(r => r.statut !== "annule").slice(0, 8).map(r => (
            <div key={r.rdv_id} className="flex items-center gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
              <div className={`w-2 h-2 rounded-full ${TYPE_COLORS[r.type_rdv] || 'bg-slate-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{r.titre}</p>
                <p className="text-xs text-slate-500">{r.date_rdv ? new Date(r.date_rdv).toLocaleDateString("fr-BE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""} {r.client_nom ? `| ${r.client_nom}` : ""}</p>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${r.statut === 'confirme' ? 'bg-emerald-100 text-emerald-700' : r.statut === 'termine' ? 'bg-slate-100 text-slate-500' : 'bg-blue-100 text-blue-700'}`}>{STATUT_LABELS[r.statut]}</span>
              <button onClick={() => openEditRdv(r)} className="text-slate-400 hover:text-blue-600"><PencilSimple size={14} /></button>
              <button onClick={() => deleteRdv(r.rdv_id)} className="text-slate-400 hover:text-red-500"><Trash size={14} /></button>
            </div>
          ))}
          {rdvs.length === 0 && <p className="text-center text-sm text-slate-400 py-4">Aucun rendez-vous ce mois</p>}
        </div>
      </div>

      {/* RDV Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingRdv ? "Modifier le RDV" : "Nouveau rendez-vous"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="form-label">Titre *</Label><Input value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })} placeholder="Ex: Essai BMW X3" data-testid="rdv-titre" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="form-label">Type</Label>
                <Select value={form.type_rdv} onValueChange={v => setForm({ ...form, type_rdv: v })}>
                  <SelectTrigger data-testid="rdv-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="essai">Essai</SelectItem>
                    <SelectItem value="livraison">Livraison</SelectItem>
                    <SelectItem value="visite">Visite</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Statut</Label>
                <Select value={form.statut} onValueChange={v => setForm({ ...form, statut: v })}>
                  <SelectTrigger data-testid="rdv-statut"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planifie">Planifie</SelectItem>
                    <SelectItem value="confirme">Confirme</SelectItem>
                    <SelectItem value="annule">Annule</SelectItem>
                    <SelectItem value="termine">Termine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="form-label">Date et heure *</Label><Input type="datetime-local" value={form.date_rdv} onChange={e => setForm({ ...form, date_rdv: e.target.value })} data-testid="rdv-date" /></div>
              <div><Label className="form-label">Duree (min)</Label><Input type="number" value={form.duree_minutes} onChange={e => setForm({ ...form, duree_minutes: parseInt(e.target.value) || 60 })} data-testid="rdv-duree" /></div>
            </div>
            <div><Label className="form-label">Client</Label><Input value={form.client_nom} onChange={e => setForm({ ...form, client_nom: e.target.value })} placeholder="Nom du client" data-testid="rdv-client" /></div>
            <div><Label className="form-label">Vehicule</Label><Input value={form.vehicle_nom} onChange={e => setForm({ ...form, vehicle_nom: e.target.value })} placeholder="Ex: BMW X3 2021" data-testid="rdv-vehicle" /></div>
            <div><Label className="form-label">Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="rdv-notes" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button onClick={saveRdv} className="btn-primary" data-testid="rdv-save-btn">{editingRdv ? "Modifier" : "Creer"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
